import React, {useState} from 'react'
import {Text, Button, Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'

/**
 * A React component that displays an initial block of SEO text with an option to expand and show additional text.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {string} props.initialSeoText - The initial SEO text to be displayed. This text is always visible.
 * @param {string} props.secondarySeoText - The additional SEO text that is displayed when the "Read more" button is clicked.
 * @param {string} props.readMoreSeoText - The text for the "Read more" button, which is displayed when there is additional text to show.
 *
 * @returns {JSX.Element} A JSX element that conditionally renders the initial and secondary SEO text, and a "Read more" button.
 *
 * @example
 * <ReadMore
 *   initialSeoText="<p>This is the initial text.</p>"
 *   secondarySeoText="<p>This is the additional text that appears after clicking 'Read more'.</p>"
 *   readMoreSeoText="Read more"
 * />
 *
 * The component uses internal state to manage whether the additional text is displayed. Initially, only the `initialSeoText` is shown.
 * If `secondarySeoText` is provided, a "Read more" button is displayed, which, when clicked, reveals the `secondarySeoText`.
 */
const ReadMore = ({initialSeoText, secondarySeoText, readMoreSeoText}) => {
    const [isExpanded, setIsExpanded] = useState(false)

    const handleReadMore = () => {
        setIsExpanded(true)
    }

    return (
        <Box marginTop={{base: '75px', lg: '50px'}} lineHeight="1">
            {initialSeoText && (
                <Text
                    dangerouslySetInnerHTML={{__html: initialSeoText}}
                    variant={'bodyBase2'}
                    color="darkGray"
                    fontSize="13px"
                    display="inline"
                    paddingRight="5px"
                />
            )}

            {secondarySeoText && isExpanded && (
                <Text
                    dangerouslySetInnerHTML={{__html: secondarySeoText}}
                    variant={'bodyBase2'}
                    color="darkGray"
                    fontSize="13px"
                    display="inline"
                />
            )}

            {!isExpanded && initialSeoText && secondarySeoText && readMoreSeoText && (
                <Button onClick={handleReadMore} variant="link" display="block">
                    <Text dangerouslySetInnerHTML={{__html: readMoreSeoText}} />
                </Button>
            )}
        </Box>
    )
}

ReadMore.propTypes = {
    initialSeoText: PropTypes.string,
    secondarySeoText: PropTypes.string,
    readMoreSeoText: PropTypes.string
}

export default ReadMore
